/**
 * Page to request new password.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import { AppRouteUrl } from '@/AppRoute'
import { RhfFormInput } from '@/components/RhfFormInput'
import '@/components/RhfFormInput.css'
import { zodResolver } from '@hookform/resolvers/zod'
import { resetPassword } from 'aws-amplify/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z as zod } from 'zod'
import './ForgotPassword.css'
import './Login.css'
import LogInButton from './LoginButton'
import LoginDialog from './LoginDialog'

/**
 * The Zod schema defining the fields and their data type.
 */
const ForgotPasswordSchema = zod.object({
  email: zod.email().trim(),
})
type ForgotPasswordType = zod.infer<typeof ForgotPasswordSchema>

interface ForgotPasswordPageProps {
  initEmail?: string
}

/**
 * The page showing the form to request the change of the forgotten password.
 */
export const ForgotPasswordPage = (props: ForgotPasswordPageProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get the needed functions defined by react-use-form
  // const formFunctionList
  const {
    register,
    getValues,
    reset,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordType>({
    mode: 'all',
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: searchParams.get('email') ?? props.initEmail ?? '',
    },
  })

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isRequestSent, setRequestSent] = useState(false)

  const handleResetPassword = handleSubmit(async (data: ForgotPasswordType) => {
    setLoading(true)
    try {
      await resetPassword({ username: data.email })
      setRequestSent(true)
    } catch (e: any) {
      setErrorMessage(e.message)
    } finally {
      setLoading(false)
    }
  })

  function goToSetPasswordPage() {
    const url =
      `${AppRouteUrl.SetPassword}?email=` +
      encodeURIComponent(getValues('email'))
    navigate(url)
  }

  const ErrorDialog = () => (
    <LoginDialog
      title="Error while sending email"
      message={errorMessage}
      onClose={() => {
        setErrorMessage(null)
        reset()
      }}
      onClick={() => {
        setErrorMessage(null)
        reset()
      }}
      open={errorMessage !== null}
    />
  )

  const AckForgotPasswordDialog = () => (
    <LoginDialog
      title="Request new password"
      message={'A temporary code has been requested. Please check your emails.'}
      onClose={goToSetPasswordPage}
      onClick={goToSetPasswordPage}
      open={isRequestSent}
    />
  )

  return (
    <>
      <div className="login-page">
        <h1 className="login-title">Forgot Your Password?</h1>
        <div>
          Please enter your email address to receive a temporary code to reset
          the password.
        </div>

        <form className="rhf-form" onSubmit={handleResetPassword}>
          <RhfFormInput
            title="Email address"
            id="email"
            type="text"
            register={register}
            error={errors.email}
          />

          <div className="login-submit-container">
            <LogInButton
              text="Reset Password"
              type="submit"
              disabled={loading || !isValid}
            />

            <LogInButton
              text="Cancel"
              toLink={AppRouteUrl.LogIn}
              disabled={loading || !isValid}
              dark={false}
            />
          </div>
        </form>
      </div>

      <AckForgotPasswordDialog />
      <ErrorDialog />
    </>
  )
}
